/**
 * @file useScaledFrame.ts
 * @description Масштаб iframe предпросмотра и связь с витриной (phase19.16
 * Р1, Р1-6а; uiux-design-r1 §1: 1280 → ~44 %, 768 → ~73 %, 360 → 1:1;
 * протокол — lib/site-view/canvas-messages.ts).
 *
 * Сайт рисуется в своей ширине (360/768/1280 — так срабатывают его
 * медиазапросы) и уменьшается `transform: scale` под ширину колонки.
 * Кабинет → витрина: документ оформления (`commora:site-theme-preview
 * { theme, shopFonts }`, CSS витрина собирает сама; свои шрифты магазина —
 * записями реестра, Р1-7) и `scroll-to { area }`.
 * Витрина → кабинет: `area-select { area }` — клик по шапке, подвалу или
 * корзине (SB120 этап 4);
 * принимается только от своего iframe.
 */

import type { SiteFontRegistry, SiteTheme } from "@chaiberi/shared";
import { useCallback, useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";

import { areaFromCanvasMessage, scrollToMessage, themeMessage, type CanvasArea } from "../../../lib/site-view/canvas-messages";
import { cartPickedOnCanvas } from "../../../lib/site-view/canvas-cart-messages";
import { previewScale } from "../../../lib/site-view/preview";

export interface ScaledFrame {
  containerRef: RefObject<HTMLDivElement | null>;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  style: CSSProperties;
  onLoad: () => void;
}

interface Options {
  /** Живое оформление (несохранённые правки); нет — витрина как есть. */
  theme?: SiteTheme;
  /**
   * Реестр шрифтов магазина с полученным списком: свои шрифты темы уходят
   * канве вместе с ней; `null` — список не получен, поле не шлём.
   */
  fontRegistry?: SiteFontRegistry | null;
  /** Область, к которой прокрутить канву. */
  area?: CanvasArea | null;
  /** Клик по шапке или подвалу на канве. */
  onAreaSelect?: (area: CanvasArea) => void;
  /** Нажатие по корзине на канве (SB120 этап 4, `area-select { area: "cart" }`). */
  onCartSelect?: () => void;
  /** `false` — масштаб 100 % (колонка прокручивается вбок), иначе «по ширине». */
  fit?: boolean;
  /** Чужой iframe (конструктор держит один на все панели). */
  iframeRef?: RefObject<HTMLIFrameElement | null>;
}

/** Отправить сообщение витрине в iframe — только на её origin (и живой предпросмотр полей оформления). */
export function postToFrame(frame: HTMLIFrameElement | null, message: object): void {
  if (!frame?.contentWindow || !frame.src) return;
  try {
    frame.contentWindow.postMessage(message, new URL(frame.src).origin);
  } catch {
    // Адрес ещё не задан или окно закрыто — следующее событие повторит.
  }
}

export function useScaledFrame(
  siteWidth: number,
  { theme, fontRegistry, area = null, onAreaSelect, onCartSelect, fit = true, iframeRef: outerRef }: Options = {},
): ScaledFrame {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ownRef = useRef<HTMLIFrameElement | null>(null);
  const iframeRef = outerRef ?? ownRef;
  const [box, setBox] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Живые правки — без перезагрузки (с короткой задержкой при протягивании ползунка цвета).
  useEffect(() => {
    if (!theme) return;
    const t = setTimeout(() => postToFrame(iframeRef.current, themeMessage(theme, fontRegistry)), 120);
    return () => clearTimeout(t);
  }, [theme, fontRegistry, iframeRef]);

  useEffect(() => {
    if (area) postToFrame(iframeRef.current, scrollToMessage(area));
  }, [area, iframeRef]);

  // Клик по шапке, подвалу или корзине на канве — только от своего iframe.
  const pick = useRef({ onAreaSelect, onCartSelect });
  pick.current = { onAreaSelect, onCartSelect };
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const selected = areaFromCanvasMessage(event.data);
      if (selected) pick.current.onAreaSelect?.(selected);
      else if (cartPickedOnCanvas(event.data)) pick.current.onCartSelect?.();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef]);

  const latest = useRef({ theme, area, fontRegistry });
  latest.current = { theme, area, fontRegistry };
  const onLoad = useCallback(() => {
    const { theme: current, area: target, fontRegistry: fonts } = latest.current;
    if (current) postToFrame(iframeRef.current, themeMessage(current, fonts));
    if (target) postToFrame(iframeRef.current, scrollToMessage(target));
  }, [iframeRef]);

  const scale = fit ? previewScale(box.width, siteWidth) : 1;
  // 100 % на узкой колонке — сайт с левого края, колонка прокручивается вбок.
  const overflow = !fit && siteWidth > box.width;
  const style: CSSProperties = {
    width: siteWidth,
    height: scale > 0 && box.height > 0 ? box.height / scale : "100%",
    transform: overflow ? "none" : `translateX(-50%) scale(${scale})`,
    left: overflow ? 0 : undefined,
  };
  return { containerRef, iframeRef, style, onLoad };
}
